/**
 * BulkProspectScanPanel — Upload → pre-scan review → START → live progress
 * → results table with filters + Promote-to-Clients.
 *
 * Reuses the existing Orange HQ visual system + hq-forms.css. Never runs
 * scans in the browser — polls the backend job every 1500ms while status
 * is "scanning".
 */
import React, { useEffect, useRef, useState } from "react";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { toast } from "sonner";
import { Upload, Play, RefreshCw, UserPlus, X, ChevronRight } from "lucide-react";
import OrangeScoreBadge, { classifyScore, CLASSIFICATION_LABEL } from "@/hq/OrangeScoreBadge";

const QUAL_STYLE = {
  HOT_PROSPECT:      { label: "HOT PROSPECT",      cls: "bg-[#C1350B] text-white" },
  STRONG_PROSPECT:   { label: "STRONG PROSPECT",   cls: "bg-[#FF6A00] text-black" },
  POTENTIAL_CLIENT:  { label: "POTENTIAL CLIENT",  cls: "bg-[#FFB347] text-black" },
  HEALTHY:           { label: "HEALTHY",           cls: "bg-[#EAEAEA] text-[#4A4A4A]" },
  NOT_SCANNED:       { label: "NOT SCANNED",       cls: "bg-[#F6F5F2] text-[#8A8A8A]" },
};
const STATUS_LABEL = {
  queued: "QUEUED", scanning: "SCANNING", complete: "COMPLETE",
  failed: "FAILED", invalid_url: "INVALID URL", skipped_duplicate: "DUPLICATE",
};

function Pill({ children, active, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.18em] uppercase ${
        active ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#EAEAEA] text-[#4A4A4A] hover:border-[#FF6A00]"
      }`}>
      {children}
    </button>
  );
}

export default function BulkProspectScanPanel({ onOpenHistory, onPromoted }) {
  const [file, setFile] = useState(null);
  const [threshold, setThreshold] = useState(75);
  const [autoAdd, setAutoAdd] = useState(false);
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [filter, setFilter] = useState("≤75");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const pollRef = useRef();

  useEffect(() => () => clearInterval(pollRef.current), []);

  const poll = async (id) => {
    try {
      const r = await hqApi.get(`/control/bulk-scan/jobs/${id}`);
      setJob(r.data.job); setCandidates(r.data.candidates);
      if (r.data.job.status !== "scanning") clearInterval(pollRef.current);
    } catch (e) { /* ignore transient */ }
  };

  const upload = async () => {
    if (!file) return toast.error("Choose a .csv or .xlsx file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("threshold", String(threshold));
      fd.append("auto_add", String(autoAdd));
      const r = await hqApi.post("/control/bulk-scan/jobs", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJob(r.data.job); setCandidates(r.data.candidates_sample);
      toast.success(`Detected ${r.data.job.total_rows} rows · ${r.data.job.valid_rows} valid`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    setBusy(false);
  };

  const start = async () => {
    if (!job) return;
    setBusy(true);
    try {
      await hqApi.post(`/control/bulk-scan/jobs/${job.id}/start`);
      pollRef.current = setInterval(() => poll(job.id), 1500);
      poll(job.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Start failed"); }
    setBusy(false);
  };

  const retry = async () => {
    setBusy(true);
    try {
      await hqApi.post(`/control/bulk-scan/jobs/${job.id}/retry-failed`);
      pollRef.current = setInterval(() => poll(job.id), 1500);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Retry failed"); }
    setBusy(false);
  };

  const promote = async (qualifiedOnly = true, allValid = false) => {
    if (!job) return;
    // "Add ALL" ⇒ send every completed scan regardless of qualification.
    // Standard ⇒ send qualified band OR explicit selection.
    const ids = (!qualifiedOnly && !allValid && selected.size > 0) ? Array.from(selected) : null;
    if (!qualifiedOnly && !allValid && (!ids || ids.length === 0))
      return toast.error("Select rows to promote");
    setBusy(true);
    try {
      const r = await hqApi.post(`/control/bulk-scan/jobs/${job.id}/promote`, {
        candidate_ids: ids,
        qualified_only: allValid ? false : qualifiedOnly,
      });
      toast.success(`Promoted ${r.data.promoted} / ${r.data.total} to Clients`);
      setSelected(new Set());
      poll(job.id);
      // Phase 3I.4P.3.3 — Auto-refresh: notify parent HQClients so the
      // underlying Clients table is refetched immediately, even if this
      // modal remains open on the results step.
      if (r.data.promoted > 0) {
        try { await onPromoted?.(r.data); } catch (_) { /* parent handles its own errors */ }
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Promote failed"); }
    setBusy(false);
  };

  const filtered = candidates.filter((c) => {
    const s = c.orange_score;
    if (filter === "ALL") return true;
    if (filter === "FAILED") return c.scan_status === "failed";
    if (filter === "≤50") return s != null && s <= 50;
    if (filter === "51–64") return s != null && s >= 51 && s <= 64;
    if (filter === "65–75") return s != null && s >= 65 && s <= 75;
    if (filter === "≤75") return s != null && s <= 75;
    if (filter === "76+") return s != null && s >= 76;
    return true;
  });

  const progressPct = job?.valid_rows ? Math.round((job.processed / job.valid_rows) * 100) : 0;

  return (
    <div data-testid="hq-bulk-scan-panel" className="space-y-5">
      {/* Setup */}
      {!job && (
        <div className="p-5 rounded-2xl bg-[#FFF7EE] border border-[#FCE1C7] space-y-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Bulk prospect scan</div>
            <h3 className="mt-1 font-black text-xl">Upload a spreadsheet of prospects</h3>
            <p className="text-[13px] text-[#4A4A4A] mt-1">
              .xlsx or .csv up to 5MB. Website column is required per row. Scans queue server-side — you can safely close this and come back.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block md:col-span-2">
              <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">File</div>
              <input type="file" accept=".csv,.xlsx" data-testid="hq-bulk-scan-file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm file:mr-3 file:px-3 file:py-2 file:rounded-full file:border-0 file:bg-[#FF6A00] file:text-black file:font-black file:text-[10px] file:tracking-[0.16em] file:uppercase"/>
            </label>
            <label className="block">
              <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Qualification threshold</div>
              <input type="number" min={0} max={100} value={threshold}
                data-testid="hq-bulk-scan-threshold"
                onChange={(e) => setThreshold(Math.max(0, Math.min(100, Number(e.target.value))))}
                className="w-full px-3 py-2 rounded-lg border border-[#EAEAEA] bg-white text-[#0A0A0A] focus:outline-none focus:border-[#FF6A00]"/>
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={autoAdd} data-testid="hq-bulk-scan-auto"
              onChange={(e) => setAutoAdd(e.target.checked)}/>
            <span>Automatically add qualified businesses to Clients when scan completes</span>
          </label>
          <div className="flex justify-end gap-2">
            {onOpenHistory && (
              <button onClick={onOpenHistory} className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase"
                data-testid="hq-bulk-scan-history-btn">Recent scans</button>
            )}
            <button onClick={upload} disabled={!file || busy} data-testid="hq-bulk-scan-upload"
              className="px-5 py-2 rounded-full bg-[#0A0A0A] text-white text-[10px] font-black tracking-[0.16em] uppercase disabled:opacity-50">
              <Upload size={13} className="inline mr-1"/> Upload & review
            </button>
          </div>
        </div>
      )}

      {/* Pre-scan review */}
      {job && job.status === "draft" && (
        <div className="p-5 rounded-2xl bg-white border border-[#EAEAEA] space-y-3" data-testid="hq-bulk-scan-review">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Pre-scan review</div>
          <div className="text-[13px]">File: <strong>{job.filename}</strong></div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ["Detected", job.total_rows],
              ["Valid", job.valid_rows],
              ["Missing website", job.missing_website],
              ["Invalid", job.invalid_website],
              ["Duplicates", job.possible_duplicates],
            ].map(([k, v]) => (
              <div key={k} className="p-3 rounded-xl bg-[#F6F5F2]">
                <div className="text-[9px] tracking-[0.24em] uppercase text-[#8A8A8A] font-bold">{k}</div>
                <div className="font-black text-2xl">{v}</div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setJob(null); setCandidates([]); setSelected(new Set()); }}
              className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase">Cancel</button>
            <button onClick={start} disabled={busy || job.valid_rows === 0} data-testid="hq-bulk-scan-start"
              className="px-5 py-2 rounded-full bg-[#FF6A00] text-black text-[10px] font-black tracking-[0.16em] uppercase disabled:opacity-50">
              <Play size={13} className="inline mr-1"/> Start bulk scan
            </button>
          </div>
        </div>
      )}

      {/* Live progress */}
      {job && (job.status === "scanning" || job.status === "complete") && (
        <div className="p-5 rounded-2xl bg-white border border-[#EAEAEA] space-y-4" data-testid="hq-bulk-scan-progress">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Bulk prospect scan</div>
              <h3 className="font-black text-2xl">
                {job.status === "scanning" ? "Scanning your prospects 🍊" : "Bulk scan complete 🍊"}
              </h3>
            </div>
            <div className="text-right">
              <div className="text-[11px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Progress</div>
              <div className="font-black text-2xl" data-testid="hq-bulk-scan-progress-count">
                {job.processed} / {job.valid_rows}
              </div>
            </div>
          </div>
          <div className="h-2 rounded-full bg-[#F6F5F2] overflow-hidden">
            <div className="h-full bg-[#FF6A00] transition-all duration-500" style={{ width: `${progressPct}%` }}/>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-[#FFF7EE]"><div className="text-[9px] tracking-[0.24em] uppercase text-[#8A8A8A] font-bold">Qualified ≤{job.qualification_threshold}</div><div className="font-black text-2xl text-[#C1350B]">{job.qualified}</div></div>
            <div className="p-3 rounded-xl bg-[#F6F5F2]"><div className="text-[9px] tracking-[0.24em] uppercase text-[#8A8A8A] font-bold">Healthy</div><div className="font-black text-2xl">{job.healthy}</div></div>
            <div className="p-3 rounded-xl bg-[#F6F5F2]"><div className="text-[9px] tracking-[0.24em] uppercase text-[#8A8A8A] font-bold">Failed</div><div className="font-black text-2xl">{job.failed}</div></div>
            <div className="p-3 rounded-xl bg-[#F6F5F2]"><div className="text-[9px] tracking-[0.24em] uppercase text-[#8A8A8A] font-bold">Remaining</div><div className="font-black text-2xl">{Math.max(0, job.valid_rows - job.processed)}</div></div>
          </div>
        </div>
      )}

      {/* Results table */}
      {job && candidates.length > 0 && (
        <div className="p-5 rounded-2xl bg-white border border-[#EAEAEA] space-y-3" data-testid="hq-bulk-scan-results">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-1.5">
              {["ALL", "≤50", "51–64", "65–75", "≤75", "76+", "FAILED"].map((f) => (
                <Pill key={f} active={filter === f} onClick={() => setFilter(f)} testId={`hq-bulk-scan-filter-${f}`}>{f}</Pill>
              ))}
            </div>
            <div className="flex gap-2">
              {job.failed > 0 && job.status === "complete" && (
                <button onClick={retry} disabled={busy}
                  className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase hover:border-[#FF6A00]"
                  data-testid="hq-bulk-scan-retry">
                  <RefreshCw size={12} className="inline mr-1"/> Retry failed
                </button>
              )}
              <button onClick={() => promote(true)} disabled={busy || job.status !== "complete"}
                data-testid="hq-bulk-scan-promote-qualified"
                className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase hover:border-[#FF6A00]">
                <UserPlus size={12} className="inline mr-1"/> Add qualified (≤{job.qualification_threshold})
              </button>
              <button onClick={() => promote(false, true)} disabled={busy || job.status !== "complete"}
                data-testid="hq-bulk-scan-promote-all"
                className="px-4 py-2 rounded-full bg-[#FF6A00] text-black text-[10px] font-black tracking-[0.16em] uppercase disabled:opacity-50">
                <UserPlus size={12} className="inline mr-1"/> Add ALL to clients
              </button>
            </div>
          </div>
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-[12px]" data-testid="hq-bulk-scan-table">
              <thead className="bg-[#F6F5F2] text-[9px] tracking-[0.18em] uppercase font-bold text-[#8A8A8A]">
                <tr>
                  <th className="text-left px-2 py-2 w-6"></th>
                  <th className="text-left px-2 py-2">Business</th>
                  <th className="text-left px-2 py-2">Website</th>
                  <th className="text-right px-2 py-2">Score</th>
                  <th className="text-left px-2 py-2">Qualification</th>
                  <th className="text-left px-2 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const q = QUAL_STYLE[c.qualification] || QUAL_STYLE.NOT_SCANNED;
                  const rowHi = (c.orange_score != null && c.orange_score <= job.qualification_threshold);
                  return (
                    <tr key={c.id} className={`border-t border-[#EAEAEA] ${rowHi ? "bg-[#FFF9F1]" : ""}`}
                        data-testid={`hq-bulk-scan-row-${c.id}`}>
                      <td className="px-2 py-2">
                        <input type="checkbox" checked={selected.has(c.id)}
                          onChange={(e) => {
                            const next = new Set(selected);
                            if (e.target.checked) next.add(c.id); else next.delete(c.id);
                            setSelected(next);
                          }}/>
                      </td>
                      <td className="px-2 py-2 font-semibold">
                        {c.business_name || c.normalized_domain || "—"}
                        {c.existing_business_id && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-[#EAEAEA] text-[#4A4A4A]">EXISTING</span>}
                        {c.promoted_to_client && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-[#0FA958] text-white">PROMOTED</span>}
                      </td>
                      <td className="px-2 py-2 text-[#4A4A4A] truncate max-w-[220px]">{c.normalized_domain || c.website || "—"}</td>
                      <td className="px-2 py-2 text-right">
                        <OrangeScoreBadge
                          score={c.orange_score}
                          size="sm"
                          testId={`hq-bulk-scan-score-${c.id}`}
                        />
                      </td>
                      <td className="px-2 py-2 text-[10px] tracking-[0.14em] uppercase font-bold">
                        {CLASSIFICATION_LABEL[classifyScore(c.orange_score)]}
                      </td>
                      <td className="px-2 py-2 text-[10px] tracking-[0.14em] uppercase text-[#8A8A8A]">{STATUS_LABEL[c.scan_status] || c.scan_status}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && <div className="py-6 text-center text-[#8A8A8A] text-sm">No rows match this filter.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
